import { useState } from 'react';

interface Template {
  id: string;
  title: string;
  category: string;
  description: string;
  image: string;
  pages: number;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  tags: string[];
}

interface TitleGeneratorProps {
  selectedTemplate: Template;
  onTitleGenerated: (data: any) => void;
  onBack: () => void;
}

const TitleGenerator = ({ selectedTemplate, onTitleGenerated, onBack }: TitleGeneratorProps) => {
  const [formData, setFormData] = useState({
    topic: '',
    targetAudience: '',
    tone: 'Professional',
    keywords: '',
    customTitle: '',
    subtitle: ''
  });
  const [generatedTitles, setGeneratedTitles] = useState<string[]>([]);
  const [selectedTitle, setSelectedTitle] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [step, setStep] = useState<'input' | 'generated' | 'final'>('input');

  const toneOptions = [
    'Professional', 'Conversational', 'Academic', 'Inspirational', 
    'Authoritative', 'Friendly', 'Persuasive', 'Educational'
  ];

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateTitles = async () => {
    if (!formData.topic.trim()) return;
    
    setIsGenerating(true);
    
    // Simulate AI title generation
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const baseTitles = [
      `The Complete Guide to ${formData.topic}`,
      `Mastering ${formData.topic}: A Step-by-Step Approach`,
      `${formData.topic} Secrets: What Experts Don't Tell You`,
      `From Beginner to Expert: Your ${formData.topic} Journey`,
      `The Ultimate ${formData.topic} Handbook`,
      `Unlocking ${formData.topic}: Proven Strategies for Success`,
      `${formData.topic} Mastery: Transform Your Understanding`,
      `The Essential ${formData.topic} Blueprint`,
      `Advanced ${formData.topic}: Next-Level Techniques`,
      `${formData.topic} Revolution: Modern Approaches`,
      `The ${formData.topic} Advantage: Competitive Strategies`,
      `Breakthrough ${formData.topic}: Innovative Methods`
    ];
    
    setGeneratedTitles(baseTitles);
    setIsGenerating(false);
    setStep('generated');
  };

  const handleTitleSelect = (title: string) => {
    setSelectedTitle(title);
  };

  const handleCustomTitle = () => {
    if (formData.customTitle.trim()) {
      setSelectedTitle(formData.customTitle);
      setStep('final');
    }
  };

  const proceedToContent = () => {
    const titleData = {
      template: selectedTemplate,
      title: selectedTitle,
      subtitle: formData.subtitle,
      topic: formData.topic,
      targetAudience: formData.targetAudience,
      tone: formData.tone,
      keywords: formData.keywords.split(',').map(k => k.trim()).filter(k => k)
    };
    
    onTitleGenerated(titleData);
  };

  const resetToInput = () => {
    setStep('input');
    setGeneratedTitles([]);
    setSelectedTitle('');
  };

  if (step === 'final') {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-3xl text-green-600"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Title Confirmed!</h1>
            <p className="text-gray-600">Your eBook title is ready. Let's move on to content generation.</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedTitle}</h2>
              {formData.subtitle && (
                <p className="text-lg text-gray-600 mb-4">{formData.subtitle}</p>
              )}
              <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
                <span className="flex items-center">
                  <i className="ri-bookmark-line mr-1"></i>
                  {selectedTemplate.category}
                </span>
                <span className="flex items-center">
                  <i className="ri-user-line mr-1"></i>
                  {formData.targetAudience}
                </span>
                <span className="flex items-center">
                  <i className="ri-chat-3-line mr-1"></i>
                  {formData.tone}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">Template Details</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• {selectedTemplate.title}</li>
                <li>• {selectedTemplate.difficulty} level</li>
                <li>• ~{selectedTemplate.pages} pages</li>
                <li>• {selectedTemplate.category} category</li>
              </ul>
            </div>
            
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="font-semibold text-green-900 mb-2">Content Focus</h3>
              <ul className="text-sm text-green-800 space-y-1">
                <li>• Topic: {formData.topic}</li>
                <li>• Audience: {formData.targetAudience}</li>
                <li>• Writing tone: {formData.tone}</li>
                {formData.keywords && <li>• Keywords: {formData.keywords}</li>}
              </ul>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep('generated')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Change Title
            </button>
            <button
              onClick={proceedToContent}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              Generate Content
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'generated') {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-lightbulb-line text-3xl text-blue-600"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">AI-Generated Titles</h1>
            <p className="text-gray-600">Choose from these AI-generated titles or create your own custom title.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {generatedTitles.map((title, index) => (
              <div
                key={index}
                onClick={() => handleTitleSelect(title)}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedTitle === title
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <h3 className="font-medium text-gray-900 flex-1">{title}</h3>
                  {selectedTitle === title && (
                    <i className="ri-check-circle-fill text-blue-600 text-lg ml-2"></i>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t pt-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Or Create Your Own Title</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Title
                </label>
                <input
                  type="text"
                  value={formData.customTitle}
                  onChange={(e) => handleInputChange('customTitle', e.target.value)}
                  placeholder="Enter your custom title..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <button
                onClick={handleCustomTitle}
                disabled={!formData.customTitle.trim()}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Use Custom Title
              </button>
            </div>
          </div>

          {selectedTitle && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-900 mb-2">Add Subtitle (Optional)</h3>
              <input
                type="text"
                value={formData.subtitle}
                onChange={(e) => handleInputChange('subtitle', e.target.value)}
                placeholder="Enter a subtitle to provide more context..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}

          <div className="flex justify-between">
            <button
              onClick={resetToInput}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Input
            </button>
            <button
              onClick={() => setStep('final')}
              disabled={!selectedTitle}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              Confirm Title
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-edit-line text-3xl text-blue-600"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Generate Your eBook Title</h1>
          <p className="text-gray-600">Tell us about your eBook topic and we'll generate compelling titles for you.</p>
        </div>

        {/* Selected Template Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-8">
          <div className="flex items-center">
            <img
              src={selectedTemplate.image}
              alt={selectedTemplate.title}
              className="w-16 h-16 rounded-lg object-cover object-top mr-4"
            />
            <div>
              <h3 className="font-semibold text-gray-900">{selectedTemplate.title}</h3>
              <p className="text-sm text-gray-600">{selectedTemplate.category} • {selectedTemplate.difficulty}</p>
            </div>
          </div>
        </div>

        <form className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What is your eBook about? *
            </label>
            <textarea
              value={formData.topic}
              onChange={(e) => handleInputChange('topic', e.target.value)}
              placeholder="Describe the main topic, subject, or theme of your eBook..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Who is your target audience? *
            </label>
            <input
              type="text"
              value={formData.targetAudience}
              onChange={(e) => handleInputChange('targetAudience', e.target.value)}
              placeholder="e.g., Small business owners, Students, Professionals, Beginners..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Writing Tone
            </label>
            <select
              value={formData.tone}
              onChange={(e) => handleInputChange('tone', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
            >
              {toneOptions.map((tone) => (
                <option key={tone} value={tone}>{tone}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Keywords (Optional)
            </label>
            <input
              type="text"
              value={formData.keywords}
              onChange={(e) => handleInputChange('keywords', e.target.value)}
              placeholder="Enter keywords separated by commas..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-sm text-gray-500 mt-1">
              Keywords help generate more targeted and SEO-friendly titles
            </p>
          </div>
        </form>

        <div className="mt-8 flex justify-between">
          <button
            onClick={onBack}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Templates
          </button>
          <button
            onClick={generateTitles}
            disabled={!formData.topic.trim() || !formData.targetAudience.trim() || isGenerating}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isGenerating ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Generating Titles...
              </>
            ) : (
              <>
                Generate Titles
                <i className="ri-arrow-right-line ml-2"></i>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TitleGenerator;